import { Post as DiscussionPost, PostState, PostType } from './DiscussionPost';

export enum DataSet {
  users = 'USERS',
  groups = 'GROUPS',
  content = 'CONTENT',
}

export enum ContentType {
  forums = 'FORUMS',
  news = 'NEWS',
}

export enum UserState {
  active = 'ACTIVE',
  archived = 'ARCHIVED',
}

export enum GroupMode {
  public = 'PUBLIC',
  private = 'PRIVATE',
}

export interface ModerationFlags {
  [key: string]: number;
}

export interface Aggregation {
  __type: string;
  aggregationName: string;
  aggregationBuckets: AggregationBucket[];
}

export interface AggregationBucket {
  bucketKey: string;
  count: number;
}

export interface Post {
  postId: string;
  contentType: ContentType;
  postType: PostType;
  threadId: string;
  replyTo: string;
  replyToUserName?: string;
  replyToUserId?: string;
  marketplaceId: string;
  createdAt: number;
  updatedAt?: number;
  title: string;
  content: string;
  contentMetadata?: DiscussionPost['contentMetadata'];
  userId: string;
  groupId?: string;
  moderationFlags?: ModerationFlags;
  updatedBy?: string;
  categoryId: string;
  totalViews: number;
  totalReplies: number;
  totalVotes: number;
  contentState: PostState;
  isAmazonReplied: boolean;
  isSolution: boolean;
  solutionId: string;
  lastActivityTime: number;
  isMigrated: boolean;
  upVotes: number;
  downVotes: number;
  tags: Array<string>;
  parentPostId?: string;
  isPinned?: boolean;
  isReplied?: boolean;
  userDisplayName: string;
  userAvatarSrc: string;
  hasSolution: boolean;
  hasAmazonReply: boolean;
}

export interface Group {
  groupId: string;
  name: string;
  description: string;
  mode: GroupMode;
  updatedBy?: string;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
  avatarSrc: string;
  totalDiscussions: number;
  totalMembers: number;
  lastActivity: number;
  totalMembersJoinedLastWeek: number;
  lastNMembersJoinedImageUrl: Array<string>;
}

export interface User {
  userId: string;
  username: string;
  avatarSrc: string;
  context?: {
    hasGroupAccess: boolean;
  }
}

export interface SearchRequest {
  // Required for Coral Class
  __type: string;
  pageSize: number;
  context?: ContextOptions,
  searchParamsPerDataSetList?: Array<SearchParamsPerDataSet>,
  listingCriteriaParamsList?: Array<SearchParamsPerDataSet>,
  marketplaceId: string;
  lastEvaluatedKeys?: Array<string>;
}

export enum SearchDataRequestType {
  SIMILAR_DISCUSSIONS = 'SIMILAR_DISCUSSIONS',
  SEARCH_DATA = 'SEARCH_DATA',
}

export interface ContextOptions {
  groupId?: string;
  requestType?: SearchDataRequestType;
}

export interface SearchResponse {
  posts: Array<Post>;
  groups: Array<Group>;
  users: Array<User>;
  aggregations?: Array<Aggregation>;
  lastEvaluatedKeys: Array<string>;
  totalCount: number;
}

export enum SearchOperatorInput {
  AND = 'AND',
  OR = 'OR',
}

export interface FilterRequest {
  __type: string;
  operator: SearchOperatorInput;
  negate?: boolean;
}

export interface SimpleFilterRequest {
  __type: string;
  key: string;
}

export enum TextComparatorInput {
  CONTAINS = 'CONTAINS',
  EQ = 'EQ',
}

export interface StringValueFilterRequest extends SimpleFilterRequest {
  phraseMatch?: boolean;
  comparator: TextComparatorInput;
  value: string;
}

export interface StringListFilterRequest extends SimpleFilterRequest {
  __type: string;
  phraseMatch?: boolean;
  comparator: TextComparatorInput;
  values: Array<string>;
}

export enum RangeComparatorInput {
  LTE = 'LTE',
  GTE = 'GTE',
  GT = 'GT',
  LT = 'LT',
  EQ = 'EQ',
}

export interface LongValueFilterRequest {
  __type: string;
  comparator: RangeComparatorInput;
  value: number;
}

export interface DoubleValueFilterRequest {
  __type: string;
  comparator: RangeComparatorInput;
  value: number;
}

export enum BoolComparatorInput {
  EQ = 'EQ',
}

export interface BooleanValueFilterRequest {
  __type: string;
  comparator: BoolComparatorInput;
  value: boolean;
}

export interface MultiMatchStringFilterRequest {
  __type: string;
  keys: Array<String>,
  comparator: TextComparatorInput,
  value: String,
}

export interface NestedFilterRequest {
  __type: string;
  filters: Array<
  | FilterRequest
  | SimpleFilterRequest
  | StringValueFilterRequest
  | StringListFilterRequest
  | LongValueFilterRequest
  | DoubleValueFilterRequest
  | BooleanValueFilterRequest
  | MultiMatchStringFilterRequest
  | NestedFilterRequest
  >
}

export interface DateRange {
  startDate?: number;
  endDate?: number;
}
export interface DateRangeFilter {
  fieldName: string;
  dateRange: DateRange;
}

export interface SearchAggregation {
  __type: string;
  aggregationName: string;
  fieldName: string;
}

export interface SortField {
  fieldName: string;
  sortAscending: boolean;
}

export interface SearchParamsPerDataSet {
  dataset: DataSet;
  searchFields?: Array<
  | FilterRequest
  | SimpleFilterRequest
  | StringValueFilterRequest
  | StringListFilterRequest
  | LongValueFilterRequest
  | DoubleValueFilterRequest
  | BooleanValueFilterRequest
  | MultiMatchStringFilterRequest
  | NestedFilterRequest
  >;
  filterFields?: Array<
  | FilterRequest
  | SimpleFilterRequest
  | StringValueFilterRequest
  | StringListFilterRequest
  | LongValueFilterRequest
  | DoubleValueFilterRequest
  | BooleanValueFilterRequest
  | MultiMatchStringFilterRequest
  | NestedFilterRequest
  >;
  dateRangeFilters?: Array<DateRangeFilter>;
  searchAggregations?: Array<SearchAggregation>;
  sortFields: Array<SortField>;
}

export enum RequestType {
  search = 'search',
  listing = 'listing',
}
